import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { limitConcurrency } from '@/lib/limitConcurrency';
import { fetchCandles } from '@/lib/tv/hlDatafeed';

const DAY_MS = 24 * 60 * 60 * 1000;
const SPARKLINE_STALE_MS = 5 * 60 * 1000;

/**
 * Sparklines are the only fan-out on the page: every market row that scrolls
 * into view asks for its own 24h of candles, and on a cold first paint that
 * was thirteen requests leaving at once (measured peak: fourteen in flight,
 * ~880ms each). They contend with each other and with the chart and balance
 * reads the user is actually waiting on. A few at a time returns each one
 * faster and finishes the set no later.
 *
 * Module-scoped on purpose — the limit is over the whole list, not per row.
 */
const SPARKLINE_LANE = limitConcurrency(4);

/**
 * 24h of hourly closes for a market-row sparkline, from Hyperliquid's public
 * candle snapshot. Fetched only while `enabled` (the row is on screen) —
 * React Query dedupes concurrent flights per coin and caches for 5 minutes,
 * and SPARKLINE_LANE bounds how many of the remaining distinct coins are in
 * flight at once.
 */
export function useMarketSparkline(coin: string, enabled: boolean) {
    return useQuery({
        queryKey: ['hl-sparkline', coin],
        enabled,
        staleTime: SPARKLINE_STALE_MS,
        retry: 1,
        queryFn: async ({ signal }) =>
            SPARKLINE_LANE(async () => {
                // Rows unmount as the list scrolls; a query cancelled while
                // queued must not spend its turn on a request nobody wants.
                if (signal.aborted) throw signal.reason;
                const end = Date.now();
                const bars = await fetchCandles(
                    coin,
                    '1h',
                    end - DAY_MS,
                    end,
                    signal,
                );
                return bars
                    .map((b) => b.close)
                    .filter((c) => Number.isFinite(c) && c > 0);
            }),
    });
}

/**
 * A row must stay on screen this long before it is worth fetching for. Flung
 * past at speed, the 348-market list marks hundreds of rows visible in a
 * couple of seconds — and the venue answers that with HTTP 429. Short enough
 * that deliberate scrolling still feels instant.
 */
const DWELL_MS = 180;

/**
 * One-shot visibility: flips true once the element has been near the viewport
 * for DWELL_MS, and stays true (a fetched sparkline should not unmount on
 * scroll-away). rootMargin pre-warms rows just below the fold.
 */
export function useOneShotVisible<T extends Element>() {
    const ref = useRef<T | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === 'undefined') return;
        let dwell: ReturnType<typeof setTimeout> | undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                const onScreen = entries.some((e) => e.isIntersecting);
                if (!onScreen) {
                    // Scrolled past before it earned a fetch — drop the timer
                    // and let a later pass start it over.
                    clearTimeout(dwell);
                    dwell = undefined;
                    return;
                }
                if (dwell !== undefined) return;
                dwell = setTimeout(() => {
                    setVisible(true);
                    observer.disconnect();
                }, DWELL_MS);
            },
            { rootMargin: '150px' },
        );
        observer.observe(el);
        return () => {
            clearTimeout(dwell);
            observer.disconnect();
        };
    }, []);

    return { ref, visible };
}
